"use client"

import React, { useEffect, useRef, useCallback } from 'react'
import TextAreaInput, { TextAreaInputProps } from '../inputs/text-area-input'
import { Control, FieldPath, FieldValues, useController } from 'react-hook-form'
import { useDirtyFields } from '../../../features/homeowners/context/dirty-fields-context'
import { useReadOnly } from '../../../features/homeowners/context/read-only-context'
import { useSession } from '@/features/auth/context/session.context'

// Custom component for textarea fields with date stamp functionality
type DateStampedTextareaProps<
    TFieldValues extends FieldValues = FieldValues, 
    TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>
> = Omit<TextAreaInputProps, 'name' | 'value' | 'onChange' | 'onBlur' | 'ref' | 'variant'> & {
    name: TName
    control: Control<TFieldValues>
    /** When true, stamp format is "MM/DD/YY User Name:" (same as Project General Notes). Default false = "MM/DD/YY:". */
    includeUserName?: boolean
}

function DateStampedTextarea<TFieldValues extends FieldValues = FieldValues, TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>>({
    name,
    control,
    inputGroupProps,
    includeUserName = false,
    ...restProps
}: DateStampedTextareaProps<TFieldValues, TName>) {
    const { isFieldDirty, isFormClean } = useDirtyFields()
    const isDirty = isFieldDirty(name as string)
    const { isReadOnly } = useReadOnly()
    const { profile } = useSession()
    
    const { field, fieldState } = useController({
        control,
        name,
    })
    
    const { error } = fieldState
    const previousValueRef = React.useRef<string>(field.value || '')
    const hasAddedDateStampRef = React.useRef<boolean>(false)
    const lastTypingTimeRef = React.useRef<number>(0)
    const previousFormCleanRef = React.useRef<boolean>(isFormClean)
    const textareaRef = useRef<HTMLTextAreaElement | null>(null)
    const pendingSelectionRef = useRef<number | null>(null)
    const justFocusedRef = useRef<boolean>(false)
    const lastStampInsertedAtRef = useRef<number>(0)

    // Reset date stamp flag when form transitions from dirty to clean (after save)
    useEffect(() => {
        // If form was dirty and is now clean, it means form was just saved
        if (previousFormCleanRef.current === false && isFormClean === true) {
            // Form was just saved - reset flag for new save session
            hasAddedDateStampRef.current = false
            lastTypingTimeRef.current = 0
        }
        previousFormCleanRef.current = isFormClean
    }, [isFormClean])
    
    // Initialize and update ref when field value changes externally
    useEffect(() => {
        const currentValue = field.value || ''
        if (previousValueRef.current !== currentValue) {
            previousValueRef.current = currentValue
        }
    }, [field.value])

    // After we insert a timestamp, set cursor to end of new content to prevent jump
    useEffect(() => {
        const position = pendingSelectionRef.current
        if (position === null) return
        pendingSelectionRef.current = null
        const raf = requestAnimationFrame(() => {
            const el = textareaRef.current
            if (el && position >= 0 && position <= (field.value || '').length) {
                el.focus()
                el.setSelectionRange(position, position)
            }
        })
        return () => cancelAnimationFrame(raf)
    }, [field.value])
    
    const setRef = useCallback((el: HTMLTextAreaElement | null) => {
        textareaRef.current = el
        if (field.ref) {
            if (typeof field.ref === 'function') field.ref(el)
            else (field.ref as React.MutableRefObject<HTMLTextAreaElement | null>).current = el
        }
    }, [field])
    
    // Format date as MM/DD/YY, optionally with user name (same as Project General Notes)
    const formatDateStamp = () => {
        const now = new Date()
        const month = String(now.getMonth() + 1).padStart(2, '0')
        const day = String(now.getDate()).padStart(2, '0')
        const year = String(now.getFullYear()).slice(-2)
        if (includeUserName) {
            const userName = profile?.fullname || 'Unknown User'
            return `${month}/${day}/${year} ${userName}:`
        }
        return `${month}/${day}/${year}:`
    }
    
    // Handle input change. Stamp is added only on Enter (in onKeyDown) to avoid
    // mobile bug where each keystroke was treated as "first input" and got a new stamp.
    const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
        const value = e.target.value
        const previousValue = previousValueRef.current
        const isTyping = value.length > previousValue.length
        const newText = value.slice(previousValue.length)

        // Enter is handled in onKeyDown; if we see newline-only here (e.g. from paste), pass through
        const isNewlineOnly = /^[\r\n]+$/.test(newText) && newText.length > 0
        if (isTyping && isNewlineOnly) {
            previousValueRef.current = value
            field.onChange(value)
            return
        }

        if (isTyping) lastTypingTimeRef.current = Date.now()
        justFocusedRef.current = false
        previousValueRef.current = value
        field.onChange(value)
    }
    
    // Intercept Enter: insert stamp on new line, unless cursor is already on an empty stamped line (avoids duplicate stamp).
    const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
        if (e.key !== 'Enter') {
            restProps.onKeyDown?.(e)
            return
        }
        const el = textareaRef.current
        const currentValue = field.value || ''
        const start = el?.selectionStart ?? currentValue.length
        const end = el?.selectionEnd ?? start
        const hasContent = currentValue.trim().length > 0
        if (!el || !hasContent) {
            restProps.onKeyDown?.(e)
            return
        }
        const dateStamp = formatDateStamp()
        const before = currentValue.slice(0, start)
        const after = currentValue.slice(end)
        const lastNewline = before.lastIndexOf('\n')
        const currentLine = lastNewline === -1 ? before : before.slice(lastNewline + 1)
        const isEmptyStampedLine = currentLine === dateStamp || currentLine === dateStamp + ' '

        e.preventDefault()
        if (isEmptyStampedLine) {
            // Already on an empty stamped line (e.g. duplicate Enter): do nothing so next keystroke types on this line
            restProps.onKeyDown?.(e)
            return
        } else {
            const newValue = `${before}\n${dateStamp} ${after}`
            lastStampInsertedAtRef.current = Date.now()
            hasAddedDateStampRef.current = true
            justFocusedRef.current = false
            previousValueRef.current = newValue
            lastTypingTimeRef.current = Date.now()
            const newCursorPos = before.length + 1 + dateStamp.length + 1
            pendingSelectionRef.current = newCursorPos
            field.onChange(newValue)
        }
        restProps.onKeyDown?.(e)
    }

    const handleFocus = (e: React.FocusEvent<HTMLTextAreaElement>) => {
        justFocusedRef.current = true
        const current = (field.value || '').trim()
        if (current === '') {
            const stamp = formatDateStamp() + ' '
            previousValueRef.current = stamp
            pendingSelectionRef.current = stamp.length
            field.onChange(stamp)
        }
        restProps.onFocus?.(e)
    }
    
    const handleBlur = (e: React.FocusEvent<HTMLTextAreaElement>) => {
        justFocusedRef.current = false
        field.onBlur()
    }
    
    return (
        <TextAreaInput 
            {...restProps}
            name={field.name}
            onChange={handleChange}
            onKeyDown={handleKeyDown}
            onFocus={handleFocus}
            onBlur={handleBlur}
            value={field.value || ""}
            ref={setRef}
            variant={error ? 'destructive' : 'default'}
            disabled={isReadOnly || restProps.disabled}
            inputGroupProps={{
                error: error?.message,
                isDirty: isDirty,
                ...inputGroupProps
            }}
        />
    )
}

export default DateStampedTextarea

